import { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import { quickBookingV2Ref } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/Modal/QuickBookingV2';
import appointmentApis from 'features/appointment/services/apis';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { getMessageError, IResponseDataBody } from 'services/response';
import styled from 'styled-components';
import { formatPhone, maskPhone } from 'utils/formatPhone';
import storage from 'utils/sessionStorage';
import toast from 'utils/toast';
import RequestAdminPWCallback, { useRequestAdminPWCallbackRef } from 'widgets/RequestAdminLocalPW/Callback';
import calendarIcon from './calendar-icon.svg';
import inCommingCallIcon from './incomming.svg';
import lockIcon from './lock-icon.svg';
import smsIcon from './sms-icon.svg';
import { ICallerIdCustomer } from './type';
import unlockIcon from './unlock-icon.svg';
import calling_icon from './calling.gif';
import regular_icon from './regular-cus.svg';
import vip_icon from './vip-cus.svg';
import new_icon from './new-cus.svg';
import infor_icon from './infor.svg';
import { Popover } from 'antd';
import { CustomerInfoPopoverUI, useCustomerInfoPopover } from 'features/user/widgets/WaitingList/WalkIn/components/CustomerName';
import { customerEditorRef } from 'features/user/widgets/WaitingList/CustomerEditor';
import { IStaffItemData } from 'services/shop/types/staff';

type Props = {
  customer?: ICallerIdCustomer | null;
  onClose: () => void;
};

const CallerIdCard = ({ customer, onClose }: Props) => {
  const modalConfirm = useModalConfirmRef();
  const navigate = useNavigate();
  const location = useLocation();
  const requestAdminPWCallbackRef = useRequestAdminPWCallbackRef();
  const [isMasked, setIsMasked] = useState(true);
  const setLoadingPage = useSetLoadingPage();

  const onBook = () => {
    onClose();
    if (!customer) return;
    quickBookingV2Ref.current?.openWithCaller(customer);
    if (!location.pathname.includes('/appointments')) {
      setTimeout(() => {
        navigate(`/store/${storage.shop_id.get()}/appointments`);
      }, 10);
    }
  };

  const handleSendSMS = async () => {
    if (!customer?.phone) return;
    setLoadingPage(true);
    try {
      const res: IResponseDataBody<boolean> = await appointmentApis.aptSendSMS(customer.phone, customer.callerId);
      if (res.data.data) {
        toast.success('SMS sent successfully');
      }
    } catch (error) {
      const msg = getMessageError(error);
      toast.error(msg);
    } finally {
      setLoadingPage(false);
    }
  };

  const onSendSMS = () => {
    if (!customer?.phone) return;
    onClose();
    modalConfirm.current?.open(`Do you want to send SMS to ${maskPhone(customer.phone)}?`, handleSendSMS);
  };

  const onToggleMasked = () => {
    if (!isMasked) return setIsMasked(true);
    requestAdminPWCallbackRef.current?.open(() => {
      setIsMasked(false);
    });
  };

  const verifyPinCode = (callback: (pin?: string, data?: IStaffItemData) => void, isNormal?: boolean) => {
    requestAdminPWCallbackRef.current?.open(callback, isNormal);
  };

  const { customerInfo, customerDetailLoading, viewCustomer, open, setOpen, handleCheckPassBeforeView, dataPin } = useCustomerInfoPopover(customer?.id || '', verifyPinCode);
  const onOpenChange = (val: boolean) => handleCheckPassBeforeView(val);
  const onEdit = () => {
    customerEditorRef.current?.openWithId(customer?.id || '', {
      addon: customerInfo,
    });
    setOpen(false);
  };

  return (
    <>
      <Container>
        <Body>
          <img src={calling_icon} alt="inCommingCallIcon" className='icon' />
          <div className='calling-container'>
            <span className='label'>Incoming call...</span>
            {!!customer?.id && <CustomerType type={customer?.customerType} />}
            <div className='cus-phone'>
              <span className='phone-label'>{customer?.name || 'Unknown name'}</span>
              <ButtonMask type='button' onClick={onToggleMasked}>
                <span className='phone-number'>{customer?.phone ? (isMasked ? maskPhone(customer.phone) : formatPhone(customer.phone)) : '--'}</span>
                <img src={isMasked ? lockIcon : unlockIcon} alt="lockIcon" style={{ width: 40, height: 40 }} />
              </ButtonMask>
            </div>
            {!!customer?.id && <Popover
              placement='rightTop'
              title={null}
              content={
                <div onClick={(e) => e.stopPropagation()}>
                  <CustomerInfoPopoverUI loading={customerDetailLoading} customerInfo={customerInfo} onOpenChange={onOpenChange} onEdit={onEdit} dataPin={dataPin} />
                </div>
              }
              open={open}
              trigger={'contextMenu'}
              onOpenChange={onOpenChange}
              overlayInnerStyle={{ padding: 16, borderRadius: 8, background: '#E4F4F6' }}
            >
              <VisitBox visit={customer?.numberVisit} onClick={viewCustomer} />
            </Popover>}
          </div>
        </Body>
        <Footer>
          <button onClick={onClose} type='button' className="btn"><span>CLOSE</span></button>
          <button onClick={onSendSMS} type='button' className="btn btn-sms"><img src={smsIcon} alt="smsIcon" /><span>Send SMS</span></button>
          <button onClick={onBook} type='button' className="btn btn-submit"><img src={calendarIcon} alt="calendarIcon" /><span>BOOK</span></button>
        </Footer>
      </Container>
      <RequestAdminPWCallback v2 ref={requestAdminPWCallbackRef} />
    </>
  );
};

const CustomerType = ({ type }: { type?: string }) => {
  if (type === 'VIP') return <CustomerTypeImg src={vip_icon} alt="vipIcon" />;
  if (type === 'NEW') return <CustomerTypeImg src={new_icon} alt="newIcon" />;
  return <CustomerTypeImg src={regular_icon} alt="regularIcon" />;
};
const CustomerTypeImg = styled.img``;

const VisitBox = ({ visit, onClick }: { visit?: number, onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void }) => {
  if (!visit) return null;
  return <VisitBoxStyled type='button' onClick={onClick}>
    <img src={infor_icon} style={{ width: 30, height: 30 }} alt="infor_icon" />
    <span>Visited #{visit}</span>
  </VisitBoxStyled>;
};

const VisitBoxStyled = styled.button`
display: flex;
padding: 4px 8px;
justify-content: center;
align-items: center;
gap: 10px;
border-radius: 4px;
background: #FDF4CE;
span {
  color: #FF7D00;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}
&:active {
  opacity: 0.5;
}
`;


const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  overflow: hidden;
  background: #F8F9FE;
`;

const ButtonMask = styled.button`
  display: flex;
  align-items: center;
`;

const Body = styled.div`
  display: flex;
  padding: 32px 24px 16px 24px;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  background: #fff;
  img.icon {
    width: 124px;
    height: 124px;
    aspect-ratio: 1/1;
  }

  .calling-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 16px;
    flex: 1 0 0;
    .label {
      color: #00A140;
      font-family: Poppins;
      font-size: 24px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }

    .cus-phone {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      align-self: stretch;
      span {
        color: #1D2129;
        font-family: Poppins;
        font-size: 36px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        &.phone-label {
          font-size: 32px;
          font-weight: 500;
        }
      }
    }
  }
`;

const Footer = styled.div`
  display: flex;
  padding: 24px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  button.btn {
    display: flex;
    padding: 14px 8px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex: 1 0 0;
    border-radius: 4px;
    border: 1px solid #CCD4DC;
    background: #CCD4DC;
    box-shadow: 0 4px 8px -3px rgba(45, 35, 66, 0.30);

    &:active {
      opacity: 0.6;
    }

    span {
      color: #1D2129;
      text-align: center;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }

    &.btn-sms {
      border: 1px solid #2D6772;
      background: #fff;
      span {
        color: #2D6772;
      }
    }

    &.btn-submit {
    border: 1px solid #00A140;
      background: #00A140;
      span {
        color: #ffffff;
      }
    }
  }
`;
export default CallerIdCard;